import converter from 'number-to-words'
import { Locale } from '@app/types'

export function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export function formatNumberWithCommas(number: number) {
  return new Intl.NumberFormat('en-NZ').format(number)
}

export function formatCurrency(value: number, locale: Locale = 'en-NZ') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: locale_currency_map[locale],
    notation: 'compact',
  }).format(value)
}

export function formatLongCurrency(value: number, locale: Locale = 'en-NZ') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: locale_currency_map[locale],
    maximumFractionDigits: 2,
  }).format(value)
}

const scaleLookUp: { [key: string]: number } = { mm: 0.001, cm: 0.01, m: 1 }

export function convertToMetersOrCentimeters(value: number, unit: string | undefined): string {
  if (!unit) return value.toString()
  if (!scaleLookUp[unit]) return `${value} ${unit}`

  const scalor = scaleLookUp[unit]
  const scaledValue = value * scalor
  if (scaledValue < 1) return `${(scaledValue * 100).toFixed(0)}cm`

  return `${scaledValue.toFixed(1)}m`
}

// Converts numbers to words in English
export function convertIntegerToWords(num: number): string {
  if (num === 0) return 'zero'
  if (num < 0) return `negative ${convertIntegerToWords(-num)}`

  return converter.toWords(num)
}

export function pluralizeIfNeeded(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

// Generated with AI
export const locale_currency_map = {
  // English
  'en-US': 'USD',
  'en-GB': 'GBP',
  'en-AU': 'AUD',
  'en-CA': 'CAD',
  'en-NZ': 'NZD',
  'en-IE': 'EUR',
  'en-ZA': 'ZAR',
  'en-IN': 'INR',
  'en-SG': 'SGD',
  'en-HK': 'HKD',
  'en-PH': 'PHP',
  'en-MY': 'MYR',
  'en-NG': 'NGN',
  'en-KE': 'KES',
  'en-GH': 'GHS',
  'en-PK': 'PKR',
  'en-JM': 'JMD',
  'en-TT': 'TTD',
  'en-BZ': 'BZD',
  'en-FJ': 'FJD',

  // German
  'de-DE': 'EUR',
  'de-AT': 'EUR',
  'de-CH': 'CHF',
  'de-LU': 'EUR',

  // French
  'fr-FR': 'EUR',
  'fr-BE': 'EUR',
  'fr-CH': 'CHF',
  'fr-CA': 'CAD',
  'fr-LU': 'EUR',
  'fr-MC': 'EUR',

  // Spanish
  'es-ES': 'EUR',
  'es-MX': 'MXN',
  'es-AR': 'ARS',
  'es-CO': 'COP',
  'es-CL': 'CLP',
  'es-PE': 'PEN',
  'es-VE': 'VES',
  'es-UY': 'UYU',
  'es-EC': 'USD',
  'es-GT': 'GTQ',
  'es-CR': 'CRC',
  'es-PA': 'PAB',
  'es-BO': 'BOB',
  'es-DO': 'DOP',
  'es-PY': 'PYG',
  'es-HN': 'HNL',
  'es-NI': 'NIO',
  'es-SV': 'USD',
  'es-CU': 'CUP',
  'es-US': 'USD',

  // Portuguese
  'pt-PT': 'EUR',
  'pt-BR': 'BRL',
  'pt-AO': 'AOA',
  'pt-MZ': 'MZN',

  // Italian
  'it-IT': 'EUR',
  'it-CH': 'CHF',

  // Dutch
  'nl-NL': 'EUR',
  'nl-BE': 'EUR',

  // Nordic
  'sv-SE': 'SEK',
  'da-DK': 'DKK',
  'nb-NO': 'NOK',
  'nn-NO': 'NOK',
  'fi-FI': 'EUR',
  'is-IS': 'ISK',

  // Eastern Europe
  'pl-PL': 'PLN',
  'cs-CZ': 'CZK',
  'sk-SK': 'EUR',
  'hu-HU': 'HUF',
  'ro-RO': 'RON',
  'bg-BG': 'BGN',
  'hr-HR': 'EUR',
  'sr-RS': 'RSD',
  'sl-SI': 'EUR',
  'uk-UA': 'UAH',
  'ru-RU': 'RUB',
  'lt-LT': 'EUR',
  'lv-LV': 'EUR',
  'et-EE': 'EUR',

  // Greek / Turkish
  'el-GR': 'EUR',
  'tr-TR': 'TRY',

  // Middle East
  'ar-SA': 'SAR',
  'ar-AE': 'AED',
  'ar-EG': 'EGP',
  'ar-QA': 'QAR',
  'ar-KW': 'KWD',
  'ar-BH': 'BHD',
  'ar-OM': 'OMR',
  'ar-JO': 'JOD',
  'ar-LB': 'LBP',
  'ar-IQ': 'IQD',
  'ar-MA': 'MAD',
  'ar-DZ': 'DZD',
  'ar-TN': 'TND',
  'ar-LY': 'LYD',
  'he-IL': 'ILS',
  'fa-IR': 'IRR',

  // South & East Asia
  'zh-CN': 'CNY',
  'zh-TW': 'TWD',
  'zh-HK': 'HKD',
  'zh-SG': 'SGD',
  'ja-JP': 'JPY',
  'ko-KR': 'KRW',
  'th-TH': 'THB',
  'vi-VN': 'VND',
  'id-ID': 'IDR',
  'ms-MY': 'MYR',
  'hi-IN': 'INR',
  'bn-BD': 'BDT',
  'ur-PK': 'PKR',
  'ta-LK': 'LKR',
  'ne-NP': 'NPR',

  // Africa (non-Arabic)
  'sw-KE': 'KES',
  'sw-TZ': 'TZS',
  'am-ET': 'ETB',
  'zu-ZA': 'ZAR',
  'af-ZA': 'ZAR',

  // Oceania / other
  'mi-NZ': 'NZD',
}
